// Route Polyline Components

import React, { useEffect, useRef } from 'react';
import { Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-polylinedecorator';

import { decodePolyline, isValidCoord, getVehicleColor } from './map-utils';
import { Route } from '../../../../api/mobiApi/domain/domain.types';


interface RoutePolylinesProps {
  routes: Route[];
  selectedRoute: string | null;
  onRouteClick: (vehicleId: string) => void;
}

export const RoutePolylines: React.FC<RoutePolylinesProps> = ({ routes, selectedRoute, onRouteClick }) => {
  const map = useMap();
  const decoratorsRef = useRef<Map<string, any>>(new Map()); // PolylineDecorator from leaflet-polylinedecorator
  const polylinesRef = useRef<Map<string, L.Polyline>>(new Map());

  // Sort routes: selected route should be rendered last (on top)
  const sortedRoutes = [...routes].sort((a, b) => {
    const aSelected = selectedRoute === a.vehicle_id;
    const bSelected = selectedRoute === b.vehicle_id;
    if (aSelected && !bSelected) return 1;
    if (!aSelected && bSelected) return -1;
    return 0;
  });

  // Update decorators and polyline styles when selection changes
  useEffect(() => {
    // Small delay to ensure polylines are rendered
    const timer = setTimeout(() => {
      // Clear old decorators
      decoratorsRef.current.forEach(decorator => decorator.remove());
      decoratorsRef.current.clear();

      // Update polyline styles and create decorators
      polylinesRef.current.forEach((polyline, vehicleId) => {
        const route = routes.find(r => r.vehicle_id === vehicleId);
        if (!route) return;

        const isSelected = selectedRoute === vehicleId;
        const hasSelection = selectedRoute !== null;

        // Update polyline style
        // If no route is selected, show all routes at medium opacity
        // If a route is selected, highlight it and dim others
        polyline.setStyle({
          weight: isSelected ? 10 : 3,
          opacity: isSelected ? 1.0 : (hasSelection ? 0.45 : 0.6)
        });

        // Only create arrows for selected route
        if (!isSelected) return;

        const vehicleIdNum = parseInt(vehicleId.replace('vehicle_', ''));
        const routeColor = getVehicleColor(vehicleIdNum);

        try {
          // Create custom arrow icon with SVG (pointing UP for proper rotation)
          const createArrowIcon = (color: string) => {
            return L.icon({
              iconUrl: 'data:image/svg+xml;base64,' + btoa(`
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
                  <defs>
                    <filter id="shadow-arrow-${vehicleId}" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
                      <feOffset dx="0" dy="2" result="offsetblur"/>
                      <feComponentTransfer>
                        <feFuncA type="linear" slope="0.5"/>
                      </feComponentTransfer>
                      <feMerge>
                        <feMergeNode/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                    <linearGradient id="arrow-grad-${vehicleId}" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" style="stop-color:${color};stop-opacity:1" />
                      <stop offset="100%" style="stop-color:${color};stop-opacity:0.8" />
                    </linearGradient>
                  </defs>
                  <!-- Arrow body pointing UP with gradient -->
                  <path d="M16 4 L8 16 L12 16 L12 28 L20 28 L20 16 L24 16 Z"
                        fill="url(#arrow-grad-${vehicleId})"
                        stroke="white"
                        stroke-width="2.5"
                        filter="url(#shadow-arrow-${vehicleId})"
                        stroke-linejoin="round"/>
                  <!-- Highlight effect on left side -->
                  <path d="M16 6 L10 14 L12 14"
                        stroke="white"
                        stroke-width="1.5"
                        opacity="0.5"
                        fill="none"
                        stroke-linecap="round"/>
                </svg>
              `),
              iconSize: [32, 32],
              iconAnchor: [16, 16]
            });
          };

          const decorator = L.polylineDecorator(polyline, {
            patterns: [
              // Modern arrow icons
              {
                offset: 50,
                repeat: 150,
                symbol: (L.Symbol as any).marker({
                  rotate: true,
                  markerOptions: {
                    icon: createArrowIcon(routeColor)
                  }
                })
              },
              // Small dash pattern for better route visualization
              {
                offset: 0,
                repeat: 30,
                symbol: L.Symbol.dash({
                  pixelSize: 8,
                  pathOptions: {
                    color: '#ffffff',
                    weight: 3,
                    opacity: 0.6
                  }
                })
              }
            ]
          });

          // Add to overlay pane for higher z-index
          decorator.addTo(map);
          decorator.bringToFront();

          console.log(`✅ Decorator created for ${vehicleId}`, decorator);
          decoratorsRef.current.set(vehicleId, decorator);
        } catch (error) {
          console.error(`❌ Error creating decorator for ${vehicleId}:`, error);
        }
      });
    }, 100);

    // Cleanup on unmount
    return () => {
      clearTimeout(timer);
      decoratorsRef.current.forEach(decorator => decorator.remove());
      decoratorsRef.current.clear();
    };
  }, [map, routes, selectedRoute]);

  return (
    <>
      {sortedRoutes.map((route) => {
        if (!route?.vehicle_id || !route.raw_shapes?.length) {
          return null;
        }

        try {
          // Decode all shapes and concatenate
          const allCoords = route.raw_shapes
            .filter(shape => shape && typeof shape === 'string')
            .flatMap(shape => decodePolyline(shape))
            .filter(coord => isValidCoord(coord));

          if (allCoords.length === 0) return null;

          const vehicleId = parseInt(route.vehicle_id.replace('vehicle_', ''));
          const routeColor = getVehicleColor(vehicleId);
          const isSelected = selectedRoute === route.vehicle_id;
          const hasSelection = selectedRoute !== null;

          return (
            <Polyline
              key={`route-${route.vehicle_id}`}
              positions={allCoords}
              color={routeColor}
              weight={isSelected ? 10 : 3}
              opacity={isSelected ? 1.0 : (hasSelection ? 0.45 : 0.6)}
              eventHandlers={{
                add: (e) => {
                  const layer = e.target as L.Polyline;
                  polylinesRef.current.set(route.vehicle_id, layer);
                },
                remove: () => {
                  polylinesRef.current.delete(route.vehicle_id);
                },
                click: () => onRouteClick(route.vehicle_id),
                mouseover: (e) => {
                  const layer = e.target;
                  if (!isSelected) {
                    layer.setStyle({ weight: 5, opacity: hasSelection ? 0.5 : 0.8 });
                  }
                },
                mouseout: (e) => {
                  const layer = e.target;
                  if (!isSelected) {
                    layer.setStyle({ weight: 3, opacity: hasSelection ? 0.45 : 0.6 });
                  }
                }
              }}
            />
          );
        } catch (error) {
          console.error(`Error rendering route ${route.vehicle_id}:`, error);
          return null;
        }
      })}
    </>
  );
};
